import React, { useState } from 'react';

interface MobilePreviewProps {
  url: string;
  title?: string;
}

type DeviceType = 'mobile' | 'tablet' | 'desktop';

interface DeviceConfig {
  name: string;
  width: number;
  height: number;
  icon: string;
}

const devices: Record<DeviceType, DeviceConfig> = {
  mobile: { name: 'Mobile (iPhone)', width: 375, height: 667, icon: '📱' },
  tablet: { name: 'Tablet (iPad)', width: 768, height: 1024, icon: '📱' },
  desktop: { name: 'Desktop', width: 1920, height: 1080, icon: '💻' }
};

export const MobilePreview: React.FC<MobilePreviewProps> = ({ url, title = 'Preview' }) => {
  const [device, setDevice] = useState<DeviceType>('mobile');
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [isOpen, setIsOpen] = useState(false);
  const [zoom, setZoom] = useState(1);

  const currentDevice = devices[device];
  const width = orientation === 'portrait' ? currentDevice.width : currentDevice.height;
  const height = orientation === 'portrait' ? currentDevice.height : currentDevice.width;

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          padding: '12px 20px',
          backgroundColor: 'var(--accent)',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 600,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        }}
      >
        <span style={{ fontSize: '18px' }}>📱</span>
        Device Preview
      </button>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) setIsOpen(false);
      }}
    >
      {/* Control Bar */}
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px 12px 0 0',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '20px',
          minWidth: Math.min(width + 40, window.innerWidth - 40) + 'px',
          maxWidth: '100%',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}
      >
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
          {title}
        </h3>

        {/* Device Selector */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {(Object.keys(devices) as DeviceType[]).map((deviceType) => (
            <button
              key={deviceType}
              onClick={() => setDevice(deviceType)}
              style={{
                padding: '8px 16px',
                backgroundColor: device === deviceType ? 'var(--accent)' : '#f0f0f0',
                color: device === deviceType ? 'white' : '#333',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
            >
              <span>{devices[deviceType].icon}</span>
              <span>{devices[deviceType].name}</span>
            </button>
          ))}
        </div>

        {/* Orientation Toggle */}
        <button
          onClick={() => setOrientation(o => o === 'portrait' ? 'landscape' : 'portrait')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#f0f0f0',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '20px',
            transition: 'transform 0.3s'
          }}
          title={`Switch to ${orientation === 'portrait' ? 'landscape' : 'portrait'}`}
        >
          {orientation === 'portrait' ? '📱' : '🔄'}
        </button>

        {/* Zoom Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#f0f0f0', borderRadius: '6px', padding: '4px 8px' }}>
          <button
            onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
            disabled={zoom <= 0.5}
            style={{
              padding: '4px 8px',
              backgroundColor: zoom <= 0.5 ? '#ddd' : 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: zoom <= 0.5 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 600
            }}
            title="Zoom Out"
          >
            −
          </button>
          <span style={{ fontSize: '12px', fontWeight: 600, minWidth: '45px', textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={() => setZoom(Math.min(2, zoom + 0.1))}
            disabled={zoom >= 2}
            style={{
              padding: '4px 8px',
              backgroundColor: zoom >= 2 ? '#ddd' : 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: zoom >= 2 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 600
            }}
            title="Zoom In"
          >
            +
          </button>
          {zoom !== 1 && (
            <button
              onClick={() => setZoom(1)}
              style={{
                padding: '4px 8px',
                backgroundColor: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '10px',
                fontWeight: 600
              }}
              title="Reset Zoom"
            >
              Reset
            </button>
          )}
        </div>

        {/* Close Button */}
        <button
          onClick={() => setIsOpen(false)}
          style={{
            padding: '8px 16px',
            backgroundColor: '#ff4444',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600
          }}
        >
          ✕ Close
        </button>
      </div>

      {/* Device Frame */}
      <div
        style={{
          backgroundColor: '#333',
          borderRadius: '0 0 12px 12px',
          padding: '20px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          overflow: 'auto',
          maxHeight: 'calc(100vh - 200px)'
        }}
      >
        <div style={{
          width: `${width}px`,
          height: `${height}px`,
          transform: `scale(${zoom})`,
          transformOrigin: 'top left',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <iframe
            src={url}
            style={{
              width: `${width}px`,
              height: `${height}px`,
              border: 'none',
              backgroundColor: 'white',
              display: 'block'
            }}
            title={`${currentDevice.name} Preview - ${orientation}`}
          />
        </div>
      </div>

      {/* Device Info */}
      <div
        style={{
          marginTop: '12px',
          color: 'white',
          fontSize: '13px',
          textAlign: 'center',
          opacity: 0.8
        }}
      >
        {width} × {height}px ({orientation})
      </div>
    </div>
  );
};

export default MobilePreview;
